import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useSpring, animated } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import RoundedSelect from '../../Forms/RoundedSelect/RoundedSelect';
import sendIcon from '../../../assets/svg/icon/hive-send-click.svg';
import './HiveEnquireForm.css';

const PACKAGE_OPTIONS = [
  { value: '', label: 'Select package of interest', secondary: true },
  { value: 'The Local Hive', label: 'The Local Hive' },
  { value: 'The Growth Hive', label: 'The Growth Hive' },
  { value: 'The Takeover Hive', label: 'The Takeover Hive' },
];

const HiveEnquireForm = ({ selectedPackage }) => {
  const [fullName, setFullName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [packageOfInterest, setPackageOfInterest] = useState(
    selectedPackage || ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const isVisible = inView || Boolean(selectedPackage);
  const bounceAnimation = useSpring({
    transform: isVisible ? 'scale(1)' : 'scale(0.8)',
    opacity: isVisible ? 1 : 0,
    config: { tension: 170, friction: 12, mass: 1 },
  });

  useEffect(() => {
    if (selectedPackage) {
      setPackageOfInterest(selectedPackage);
    }
  }, [selectedPackage]);

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL
        ? `${process.env.REACT_APP_API_BASE_URL}/websiteHiveContact`
        : 'https://nijhzh7x7g.execute-api.us-east-1.amazonaws.com/prelive/websiteHiveContact';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: fullName,
          email,
          businessName,
          website,
          phone: phone || '',
          packageOfInterest: packageOfInterest || '',
          source: 'hive',
        }),
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      toast.success('Thank you! Your enquiry has been sent.');
      setSucceeded(true);
      setFullName('');
      setBusinessName('');
      setWebsite('');
      setEmail('');
      setPhone('');
      setPackageOfInterest('');
    } catch (error) {
      toast.error(
        'Sorry, there was a problem! Please contact us at thehive@edcsquared.io'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <animated.div ref={ref} style={bounceAnimation} className="hive-enquire">
      <div className="hive-enquire-copy">
        <h2 className="hive-enquire-heading" id="hive-enquire-heading">
          Interested to learn more about EDC Hive?
        </h2>
        <p className="hive-enquire-subheading">
          Complete this form and we will be in touch.
        </p>
      </div>

      {succeeded ? (
        <p className="hive-enquire-success">
          Thank you! Your enquiry has been sent.
        </p>
      ) : (
        <form className="hive-enquire-form" onSubmit={handleSubmit}>
          <label className="hive-enquire-field">
            <span>
              Full Name <span className="hive-enquire-required">*</span>
            </span>
            <input
              type="text"
              value={fullName}
              onChange={event => setFullName(event.target.value)}
              placeholder="e.g. Jane Smith"
              required
            />
          </label>
          <label className="hive-enquire-field">
            <span>
              Name of your Business <span className="hive-enquire-required">*</span>
            </span>
            <input
              type="text"
              value={businessName}
              onChange={event => setBusinessName(event.target.value)}
              placeholder="e.g. Sunrise Coffee Co."
              required
            />
          </label>
          <label className="hive-enquire-field">
            <span>
              Website <span className="hive-enquire-required">*</span>
            </span>
            <input
              type="text"
              value={website}
              onChange={event => setWebsite(event.target.value)}
              placeholder="e.g. www.yourbusiness.co.za"
              required
            />
          </label>
          <label className="hive-enquire-field">
            <span>
              Email <span className="hive-enquire-required">*</span>
            </span>
            <input
              type="email"
              value={email}
              onChange={event => setEmail(event.target.value)}
              placeholder="e.g. jane@yourbusiness.co.za"
              required
            />
          </label>
          <label className="hive-enquire-field">
            <span>Phone Number</span>
            <input
              type="tel"
              value={phone}
              onChange={event => setPhone(event.target.value)}
              placeholder="e.g. 082 123 4567"
            />
          </label>
          <RoundedSelect
            label="Package of Interest"
            placeholder="Select package of interest"
            value={packageOfInterest}
            options={PACKAGE_OPTIONS}
            onChange={setPackageOfInterest}
            overlayDropdown
          />
          <button
            className="hive-enquire-submit"
            type="submit"
            disabled={submitting}
          >
            <img src={sendIcon} alt="" className="hive-enquire-submit-icon" />
            {submitting ? 'Sending...' : 'Send'}
          </button>
        </form>
      )}
    </animated.div>
  );
};

export default HiveEnquireForm;
